import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { NotificationManager } from "react-notifications";
import AssignSlatesDialog from "../../../common/AssignSlatesDialog";
import AssignTestsDialog from "../../../common/AssignTestsDialog";
import DeleteConfirmation from "../../../common/DeleteConfirmation";
import { GetSlateTests } from "../../../../services/AssignSlatesService";
import {
  RemoveSlateTests,
  ReorderSlateTests,
} from "../../../../services/TestLibService";

/**
 * Tests assigned to a slate - the React equivalent of the gvAssignedTests grid on the
 * VLP testslates.aspx page, including its Assign Tests / Assign Slates / Re-order Tests
 * actions. Tests are read through the shared assign-slates endpoint that the assign
 * dialogs already use, and added through the shared Tests and Slates selector dialogs.
 */
const SlateTests = ({ jobId }) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [selectedTestCodes, setSelectedTestCodes] = useState([]);
  const [addTestsOpen, setAddTestsOpen] = useState(false);
  const [copyFromSlateOpen, setCopyFromSlateOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState([]);

  const slateId = (jobId || "").trim();

  const loadTests = useCallback(async () => {
    if (!slateId) return;

    setLoading(true);
    try {
      const response = await GetSlateTests(slateId);
      const results = response?.results ?? response ?? [];
      setTests(Array.isArray(results) ? results : []);
      setSelectedTestCodes([]);
    } catch (error) {
      NotificationManager.error(
        "Error loading tests for this slate. " + error,
        "Error"
      );
    } finally {
      setLoading(false);
    }
  }, [slateId]);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  // Client-side filter: a slate holds a few dozen tests at most, so there is no reason
  // to round-trip to the server for this.
  const isFiltered = filterText.trim().length > 0;
  const visibleTests = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    if (!needle) return tests;
    return tests.filter((test) =>
      [test.testCode, test.parameter, test.testUnits, test.methodCode]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle))
    );
  }, [tests, filterText]);

  const visibleSelectedCount = visibleTests.filter((test) =>
    selectedTestCodes.includes(test.testCode)
  ).length;

  const toggleTest = (testCode) => {
    setSelectedTestCodes((previous) =>
      previous.includes(testCode)
        ? previous.filter((code) => code !== testCode)
        : [...previous, testCode]
    );
  };

  const toggleAllVisible = () => {
    const visibleCodes = visibleTests.map((test) => test.testCode);
    if (visibleSelectedCount === visibleCodes.length) {
      setSelectedTestCodes((previous) =>
        previous.filter((code) => !visibleCodes.includes(code))
      );
    } else {
      setSelectedTestCodes((previous) => [
        ...previous.filter((code) => !visibleCodes.includes(code)),
        ...visibleCodes,
      ]);
    }
  };

  const removeTests = async (testCodes) => {
    if (testCodes.length === 0) return;

    setBusy(true);
    try {
      const response = await RemoveSlateTests(slateId, testCodes);
      const removedCount = response?.results ?? testCodes.length;
      NotificationManager.success(
        `${removedCount} test${removedCount === 1 ? "" : "s"} removed from slate ${slateId}.`,
        "Success"
      );
      await loadTests();
    } catch (error) {
      NotificationManager.error(
        "Error removing tests from this slate. " + error,
        "Error"
      );
    } finally {
      setBusy(false);
      setPendingRemoval([]);
    }
  };

  const moveTest = async (index, offset) => {
    const targetIndex = index + offset;
    if (targetIndex < 0 || targetIndex >= tests.length) return;

    const reordered = [...tests];
    [reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ];

    // Show the move straight away, then persist it; reload on failure so the grid
    // never disagrees with what is stored.
    setTests(reordered);
    setBusy(true);
    try {
      await ReorderSlateTests(
        slateId,
        reordered.map((test) => test.testCode)
      );
      await loadTests();
    } catch (error) {
      NotificationManager.error(
        "Error saving the test order. " + error,
        "Error"
      );
      await loadTests();
    } finally {
      setBusy(false);
    }
  };

  const handleAssignComplete = async () => {
    await loadTests();
  };

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.primary.main,
    color: "white",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  };

  if (!slateId) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
          Assigned Tests
        </Typography>
        <Chip size="small" label={`${tests.length} total`} />
        {selectedTestCodes.length > 0 && (
          <Chip
            size="small"
            color="primary"
            label={`${selectedTestCodes.length} selected`}
          />
        )}

        <Box sx={{ flexGrow: 1 }} />

        <TextField
          size="small"
          placeholder="Filter tests"
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220 }}
        />
        <Tooltip title="Reload tests">
          <IconButton size="small" onClick={loadTests} disabled={loading || busy}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<ContentCopyIcon />}
          onClick={() => setCopyFromSlateOpen(true)}
          disabled={busy}
          style={{ whiteSpace: "nowrap" }}
        >
          Copy From Slate
        </Button>
        <Button
          variant="contained"
          size="medium"
          startIcon={<AddIcon />}
          onClick={() => setAddTestsOpen(true)}
          disabled={busy}
          style={{ whiteSpace: "nowrap" }}
        >
          Add Tests
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="medium"
          startIcon={<DeleteIcon />}
          onClick={() => setPendingRemoval(selectedTestCodes)}
          disabled={busy || selectedTestCodes.length === 0}
          style={{ whiteSpace: "nowrap" }}
        >
          Remove Selected
        </Button>
      </Box>

      <TableContainer sx={{ maxHeight: 460 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={headerCellSx}>
                <Checkbox
                  size="small"
                  sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
                  checked={
                    visibleTests.length > 0 &&
                    visibleSelectedCount === visibleTests.length
                  }
                  indeterminate={
                    visibleSelectedCount > 0 &&
                    visibleSelectedCount < visibleTests.length
                  }
                  onChange={toggleAllVisible}
                  disabled={visibleTests.length === 0}
                  inputProps={{ "aria-label": "Select all tests" }}
                />
              </TableCell>
              <TableCell sx={headerCellSx}>Test Code</TableCell>
              <TableCell sx={headerCellSx}>Parameter</TableCell>
              <TableCell sx={headerCellSx}>Units</TableCell>
              <TableCell sx={headerCellSx}>Method</TableCell>
              <TableCell sx={headerCellSx}>Order</TableCell>
              <TableCell sx={headerCellSx} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={26} />
                </TableCell>
              </TableRow>
            )}

            {!loading && visibleTests.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {tests.length === 0
                      ? "No tests are assigned to this slate yet. Use Add Tests or Copy From Slate."
                      : "No tests match the current filter."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              visibleTests.map((test, visibleIndex) => {
                const index = tests.indexOf(test);
                return (
                  <TableRow
                    key={test.testCode}
                    hover
                    selected={selectedTestCodes.includes(test.testCode)}
                    sx={{
                      backgroundColor:
                        visibleIndex % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedTestCodes.includes(test.testCode)}
                        onChange={() => toggleTest(test.testCode)}
                        inputProps={{
                          "aria-label": `Select test ${test.testCode}`,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {test.testCode}
                    </TableCell>
                    <TableCell>{test.parameter}</TableCell>
                    <TableCell>{test.testUnits}</TableCell>
                    <TableCell>{test.methodCode}</TableCell>
                    <TableCell>{test.sortOrder}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {/* Re-ordering swaps adjacent rows, so it is only meaningful
                          while the full list is on screen. */}
                      <Tooltip
                        title={
                          isFiltered
                            ? "Clear the filter to re-order tests"
                            : "Move up"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => moveTest(index, -1)}
                            disabled={busy || isFiltered || index === 0}
                            aria-label={`Move ${test.testCode} up`}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          isFiltered
                            ? "Clear the filter to re-order tests"
                            : "Move down"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => moveTest(index, 1)}
                            disabled={
                              busy || isFiltered || index === tests.length - 1
                            }
                            aria-label={`Move ${test.testCode} down`}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove from slate">
                        <span>
                          <IconButton
                            size="small"
                            sx={{ color: "error.main" }}
                            onClick={() => setPendingRemoval([test.testCode])}
                            disabled={busy}
                            aria-label={`Remove ${test.testCode} from slate`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Shared Tests selector - assigns individual tests from the test library */}
      <AssignTestsDialog
        open={addTestsOpen}
        onClose={() => setAddTestsOpen(false)}
        itemType="Slate"
        itemLabel="Slate:"
        itemValue={slateId}
        matrix="All"
        onAssignComplete={handleAssignComplete}
      />

      {/* Shared Slates selector - copies the tests of another slate onto this one */}
      <AssignSlatesDialog
        open={copyFromSlateOpen}
        onClose={() => setCopyFromSlateOpen(false)}
        mode="SLATE"
        slateId={slateId}
        onAssignComplete={handleAssignComplete}
      />

      <DeleteConfirmation
        isOpen={pendingRemoval.length > 0}
        onClose={() => setPendingRemoval([])}
        onConfirm={() => removeTests(pendingRemoval)}
        title="Remove Tests"
        message={
          pendingRemoval.length === 1
            ? `Remove test ${pendingRemoval[0]} from slate ${slateId}?`
            : `Remove ${pendingRemoval.length} tests from slate ${slateId}?`
        }
        color="red"
      />
    </Paper>
  );
};

export default SlateTests;
